import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';
import { FormEvent, useId, useMemo, useState } from 'react';
import {
  useAuthUserRestaurant,
  useCreateProductOfferOrderMutation,
  ValidationError,
} from '@durma-soft/gros-sdk';
import { ProductOffer } from '@durma-soft/gros-sdk/dist/types/product-offers';

import { Modal } from '@/components/shared/modal';
import { Input } from '@/components/shared/shadcn-ui/input';
import { Label } from '@/components/shared/shadcn-ui/label';
import { Button } from '@/components/shared/shadcn-ui/button';
import { DialogFooter } from '@/components/shared/shadcn-ui/dialog';
import { ConfirmationModal } from '@/components/shared/confirmation-modal';

import { formatPrice } from '@/utils/product-offers';

interface OrderProductOfferModalProps {
  offer: ProductOffer;
  onClose: () => unknown;
}

export const OrderProductOfferModal = ({
  offer,
  onClose,
}: OrderProductOfferModalProps) => {
  const { t } = useTranslation();
  const uid = useId();
  const restaurant = useAuthUserRestaurant();

  const [quantity, setQuantity] = useState<number | null>(1);
  const [isConfirmationOpen, setIsConfirmationOpen] = useState(false);

  const createOrder = useCreateProductOfferOrderMutation();

  const isValid = useMemo(() => quantity != null && quantity > 0, [quantity]);

  const total = useMemo(() => {
    if (!isValid) return null;
    return (Number(offer.price_per_unit) * quantity!).toFixed(2);
  }, [isValid, quantity, offer.price_per_unit]);

  const handleSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!isValid) return;
    setIsConfirmationOpen(true);
  };

  const confirmOrder = async () => {
    try {
      await createOrder.mutateAsync({
        restaurant_id: restaurant.id,
        product_offer_id: offer.id,
        quantity: quantity!,
      });
      setIsConfirmationOpen(false);
      onClose();
      toast.success(t('product-offers.order-success-msg'));
    } catch (error) {
      setIsConfirmationOpen(false);
      if (error instanceof ValidationError) {
        return toast.error(error.message);
      }
      toast.error(t('product-offers.order-error-msg'));
    }
  };

  return (
    <>
      <Modal
        isModalOpen
        closeModal={onClose}
        title={t('product-offers.order-title')}
        description={t('product-offers.order-description', {
          productOffer: offer.name,
        })}
      >
        <form onSubmit={handleSubmit}>
          <div className="grid gap-4 py-4">
            <div className="flex flex-col gap-2">
              <Label htmlFor={uid + '-quantity'}>
                {t('common.quantity')} ({offer.unit})
              </Label>
              <Input
                id={uid + '-quantity'}
                min={0.01}
                step="any"
                type="number"
                inputMode="decimal"
                value={quantity ?? ''}
                onChange={(e) =>
                  setQuantity(e.target.value === '' ? null : +e.target.value)
                }
              />
            </div>
            <p className="text-sm text-muted-foreground">
              {t('product-offers.price-per-unit')}:{' '}
              {formatPrice(offer.price_per_unit)} {offer.currency}
              {total && (
                <>
                  {' — '}
                  {t('product-offers.total')}: {total} {offer.currency}
                </>
              )}
            </p>
          </div>
          <DialogFooter className="mt-2">
            <Button type="submit" disabled={!isValid}>
              {t('product-offers.order')}
            </Button>
          </DialogFooter>
        </form>
      </Modal>
      <ConfirmationModal
        isLoading={createOrder.isPending}
        isModalOpen={isConfirmationOpen}
        confirmationText={t('product-offers.order')}
        title={t('product-offers.order-confirmation-title')}
        description={t('product-offers.order-confirmation-description', {
          quantity,
          unit: offer.unit,
          productOffer: offer.name,
          total,
          currency: offer.currency,
        })}
        onConfirm={confirmOrder}
        onCancel={() => setIsConfirmationOpen(false)}
        closeModal={() => setIsConfirmationOpen(false)}
      />
    </>
  );
};
